import { useSeo } from "@/lib/seo";
import LegalLayout from "@/components/LegalLayout";

export default function Terms() {
    useSeo({
        title: "Conditions générales d'utilisation — Coway",
        description:
            "Conditions générales d'utilisation de l'application Coway, service de mise en relation pour le partage de trajets en taxi, VTC et Uber.",
        canonical: "https://coway.fr/terms",
    });

    return (
        <LegalLayout
            kicker="Légal"
            title="Conditions générales d'utilisation"
            lastUpdated="1er février 2026"
            testId="terms-page"
        >
            <p>
                Les présentes Conditions Générales d'Utilisation (« CGU »)
                régissent l'accès et l'utilisation de l'application mobile
                Coway et du site coway.fr (les « Services »), éditée par Coway,
                France.
            </p>

            <h2>1. Objet</h2>
            <p>
                Coway est une plateforme de mise en relation permettant à des
                utilisateurs majeurs de se rencontrer pour partager un trajet
                en taxi, VTC ou Uber lorsqu'ils se déplacent dans la même
                direction. <strong>Coway n'est ni transporteur, ni opérateur
                de VTC, ne vend pas de prestation de transport et ne traite
                aucun paiement.</strong> Le service se limite à faciliter la
                rencontre et la coordination entre voyageurs.
            </p>

            <h2>2. Inscription et compte</h2>
            <p>
                L'utilisation des Services nécessite la création d'un compte.
                Vous devez :
            </p>
            <ul>
                <li>être âgé d'au moins 18 ans,</li>
                <li>fournir des informations exactes et à jour,</li>
                <li>maintenir la confidentialité de vos identifiants,</li>
                <li>
                    vous soumettre à la vérification d'identité demandée par
                    Coway.
                </li>
            </ul>

            <h2>3. Utilisation des Services</h2>
            <p>Vous vous engagez à :</p>
            <ul>
                <li>utiliser les Services de bonne foi,</li>
                <li>
                    respecter les autres utilisateurs (politesse, ponctualité,
                    propreté),
                </li>
                <li>
                    régler directement votre part de la course auprès des
                    autres passagers ou du chauffeur, selon les modalités que
                    vous aurez convenues entre vous,
                </li>
                <li>
                    ne pas utiliser les Services à des fins commerciales,
                    frauduleuses ou illégales,
                </li>
                <li>
                    ne pas usurper l'identité d'un tiers ni créer plusieurs
                    comptes.
                </li>
            </ul>

            <h2>4. Rôle de Coway</h2>
            <p>
                Coway agit exclusivement comme intermédiaire technique de mise
                en relation. Le contrat de transport est conclu directement
                entre l'utilisateur et le chauffeur du taxi, VTC ou Uber.{" "}
                <strong>Coway n'est pas partie à ce contrat, ne perçoit
                aucune somme, et n'est pas responsable de l'exécution du
                trajet</strong> par le transporteur ni des arrangements
                financiers conclus entre passagers.
            </p>

            <h2>5. Gratuité et partage des frais</h2>
            <p>
                L'application Coway est <strong>gratuite</strong> à télécharger
                et à utiliser. Coway est exclusivement une plateforme de mise
                en relation : <strong>aucun paiement n'est traité par
                l'application</strong>, aucune commission n'est prélevée,
                aucun encaissement n'est effectué.
            </p>
            <p>
                Le partage des frais de course (taxi, VTC, Uber) s'organise{" "}
                <strong>librement et directement entre passagers</strong>, à
                leur convenance et sous leur seule responsabilité. Coway peut
                proposer une estimation indicative de la part de chacun, mais
                ne participe à aucun moment au flux financier.
            </p>
            <p>
                Les utilisateurs sont seuls responsables de leurs accords
                financiers et de leur exécution. Tout litige relatif au
                paiement de la course concerne les passagers entre eux et/ou
                le transporteur, sans intervention possible de Coway.
            </p>

            <h2>6. Annulation et no-show</h2>
            <p>
                Coway encourage chaque utilisateur à prévenir les autres
                passagers en cas d'empêchement le plus tôt possible. Les
                annulations répétées ou absences (« no-show ») peuvent
                impacter votre score de fiabilité au sein de la communauté.
                Aucune sanction financière n'est appliquée par Coway.
            </p>

            <h2>7. Évaluations et signalements</h2>
            <p>
                Après chaque trajet, vous pouvez évaluer les autres passagers.
                Tout comportement inapproprié peut être signalé via
                l'application. Coway peut suspendre ou résilier un compte en
                cas de manquement répété aux CGU.
            </p>

            <h2>8. Propriété intellectuelle</h2>
            <p>
                L'ensemble des éléments composant les Services (marque,
                logiciel, interface, contenus) est la propriété exclusive de
                Coway ou de ses partenaires. Toute reproduction non autorisée
                est interdite.
            </p>

            <h2>9. Données personnelles</h2>
            <p>
                Le traitement de vos données est décrit dans notre{" "}
                <a href="/privacy-policy">Politique de confidentialité</a>.
            </p>

            <h2>10. Responsabilité</h2>
            <p>
                Coway met tout en œuvre pour assurer la disponibilité et la
                sécurité des Services mais ne peut garantir une absence totale
                d'interruption ou d'erreur. La responsabilité de Coway est
                limitée aux dommages directs et prévisibles.
            </p>

            <h2>11. Résiliation</h2>
            <p>
                Vous pouvez résilier votre compte à tout moment via la page{" "}
                <a href="/delete-account">Suppression de compte</a>. Coway peut
                résilier votre compte en cas de violation grave des CGU.
            </p>

            <h2>12. Droit applicable et juridiction</h2>
            <p>
                Les présentes CGU sont régies par le droit français. Tout
                litige relatif à leur interprétation ou à leur exécution sera,
                à défaut d'accord amiable, soumis aux tribunaux français
                compétents.
            </p>

            <h2>13. Modifications</h2>
            <p>
                Coway se réserve le droit de modifier les présentes CGU à tout
                moment. Les utilisateurs seront informés des modifications
                substantielles via l'application.
            </p>

            <h2>14. Contact</h2>
            <p>
                Pour toute question :{" "}
                <a href="mailto:cowayfrance@gmail.com">cowayfrance@gmail.com</a>.
            </p>
        </LegalLayout>
    );
}
